/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2020 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import LimitedAction from '../Utility/Component/Action/LimitedAction';
import SmartPrefab from '../Utility/Prefab/SmartPrefab';
import SplashScreenComponent from './SplashScreenComponent';

/** @hidden */
const property: typeof cc._decorator.property = cc._decorator.property;

/** [[SplashScreenComponent]] specifically for web and using [[HTMLElement]]. */
@cc._decorator.ccclass
export default class HTMLSplashScreenComponent extends SplashScreenComponent
{
    /** ID for WebElement for splash. */
    @property
    protected splashID: string = 'splash';

    @property(cc.Integer)
    protected loadingUpdateFreq: number = 5;

    @property(cc.Integer)
    protected maxBufferProgress: number = 70;

    @property
    @SmartPrefab.CustomProperty()
    protected debugLoading: boolean = true;

    /** [[HTMLElement]] for splash screen. */
    protected splashScreen: HTMLElement = null;
    /** [[HTMLElement]] for progress bar. */
    protected progressBar: HTMLElement = null;
    protected originalProgressBar: HTMLElement = null;

    private _updateAction: LimitedAction;

    private _fnDebug: (progress: string) => void = p => null;

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        if (CC_JSB) { return; }

        this.configureSplashScreen();
        if (CC_DEBUG) { this.configureDebug(); }

        this._updateAction = new LimitedAction(() => this.updateHtmlUI(), this.loadingUpdateFreq);

        this.updateAssetLoading();
        this.loadingInfo.OnProgressChanged(progress => this.setProgress(progress));
    }

    private configureDebug(): void
    {
        this.showSplashScreen();

        const span: HTMLElement = document.createElement('span');
        span.innerHTML = '0';
        this.progressBar.appendChild(span);

        this._fnDebug = progress => span.innerHTML = progress;
    }

    /** @hidden */
    protected updateUI(): void
    {
        if (CC_JSB) { return; }

        this.updateAssetLoading();
        this._updateAction.Execute();
    }

    private updateHtmlUI(): void
    {
        this.updateLoadingProgress();

        this.recoveryManagerRef.WithValue(recoveryManager =>
            {
                const percent: number = recoveryManager.RecoveryCurrentPlayID
                    / Math.max(recoveryManager.RecoveryTargetPlayID, 1);
                this.recoveryLoadingInfo.SetProgress(percent * 100);
            });

        if (CC_DEBUG && !this.debugLoading) { return; }
        this.setSplashDisplay(false);
    }

    private updateAssetLoading(): void
    {
        this.loadingInfo.SetProgress(this.getCssProgress(this.originalProgressBar.style));
    }

    public Hide(override: boolean): void
    {
        if (CC_JSB) { return; }

        super.Hide(override);
    }

    /** @hidden */
    protected showSplashScreen(): void
    {
        if (!this.debugLoading) { return; }
        this.scheduleOnce(() =>
        {
            this.setSplashDisplay(false, true);
        });
    }

    /** @hidden */
    protected hideSplashScreen(): void
    {
        this.removeLoadHandler();
        this.scheduleOnce(() =>
        {
            this.setSplashDisplay(true, false);
        }, this.dismissDelaySS);
    }

    private configureSplashScreen(): void
    {
        this.splashScreen = document.getElementById(this.splashID);
        if (!this.splashScreen) { return; }

        if (CC_DEBUG && this.debugAlpha !== SplashScreenComponent.MAX_ALPHA)
        {
            this.splashScreen.style.opacity = `${(this.debugAlpha /  SplashScreenComponent.MAX_ALPHA).toFixed(4)}`;
        }

        const everiClass: string = 'everi-progress-bar';
        const classAttr: string = 'class';

        this.originalProgressBar = this.splashScreen.querySelector('.progress-bar span');
        if (!this.originalProgressBar) { return; }

        let everiBar: HTMLElement = this.splashScreen.querySelector(`.${everiClass}`);
        if (!everiBar)
        {
            everiBar = this.originalProgressBar.cloneNode(true) as HTMLElement;
            everiBar.style.width = '0%';
            everiBar.setAttribute(classAttr, everiClass);

            this.originalProgressBar.setAttribute(classAttr, 'hidden');
            this.originalProgressBar.parentElement.insertBefore(everiBar, this.originalProgressBar);
        }
        this.progressBar = everiBar;
    }

    /**
     * Sets styling of progress bar to match given progress.
     * @param toProgress Percent to change styling to match.
     */
    private setProgress(toProgress: number): void
    {
        if (!this.progressBar) { return; }

        const currentProgress: number = this.getBarProgress();
        if (toProgress < currentProgress) { return; }
        if (toProgress > this.maxBufferProgress) { this.removeLoadHandler(); }

        const progressStyle: string = `${toProgress.toFixed(2)}%`;

        this.progressBar.style.width = progressStyle;
        this._fnDebug(progressStyle);
    }

    private removeLoadHandler(): void
    {
        const progressLoader: IProgressLoader = cc.loader as any;
        if (!progressLoader.onProgress) { return; }
        delete (progressLoader).onProgress;
    }

    private getBarProgress(): number
    {
        return this.getCssProgress(this.progressBar.style);
    }

    private getCssProgress(style: CSSStyleDeclaration): number
    {
        return parseFloat(style.width.split('%')[0]);
    }

    /**
     * Sets the splash style diplay type.
     * @param hidden Display CSS property.
     * @param componentEnabled If false, will disable this component.
     */
    private setSplashDisplay(hidden: boolean, componentEnabled: boolean = null): void
    {
        if (this.isHidden && hidden) { return; }
        if (!this.splashScreen) { return; }

        this.splashScreen.style.display = hidden ? 'none' : 'flex';

        if (componentEnabled != null) { this.enabled = componentEnabled; }
    }
}

interface IProgressLoader
{
    onProgress: (current: number, total: number) => void;
}
